import React, { useCallback, useEffect, useMemo, useState } from "react"
import { message, Spin } from "antd"
import { ReferralsApi } from "../../Apis/referralsApi"
import { translations } from "../../utils/Translation/staticTranslation"

const displayCustomCodeStatus = (label, lang, t) => {
  const s = String(label || "")
    .trim()
    .toLowerCase()
  if (lang === "ar") {
    if (s === "active") return t.status_active
    if (s === "inactive") return t.status_inactive
  }
  return label
}

const formatConfigSarPrice = (value) => {
  const n = parseFloat(value)
  if (!Number.isFinite(n)) return "SAR 0"
  const text = Number.isInteger(n)
    ? String(n)
    : n.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
  return `SAR ${text}`
}

const formatRewardPercent = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : Number(value)
  if (!Number.isFinite(n)) return "0%"
  return `${parseFloat(n.toFixed(2))}%`
}

const formatAnnualRewardPercent = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : Number(value)
  if (Number.isFinite(n) && n === 0) return "-"
  return formatRewardPercent(value)
}

const isArabicLang = (lang) =>
  String(lang || "").toLowerCase().startsWith("ar")

const mapRewardInfoRow = (row, lang) => {
  const plan = isArabicLang(lang)
    ? String(row.plan_arabic || "").trim() || row.plan_name || "—"
    : row.plan_name || "—"
  return {
    key: row.id,
    plan,
    price: formatConfigSarPrice(row.price_sar),
    monthly: formatRewardPercent(row.monthly_reward_percent),
    annual: formatAnnualRewardPercent(row.annual_reward_percent),
  }
}

const mapCustomCodeRow = (row) => ({
  key: row.id,
  code: row.referral_code,
  influencer:
    String(row.influencer_email ?? row.email ?? "")
      .trim() || "—",
  statusLabel: row.status || (row.is_active ? "Active" : "Inactive"),
  isActive: row.is_active !== false,
})

const isValidEmail = (value) => {
  const s = String(value ?? "").trim()
  if (!s) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
}

/** Server may return `details` as a Python repr string with ErrorDetail(string='…', code=…). */
const messageFromReferralErrorPayload = (data) => {
  if (!data || typeof data !== "object") return ""
  const pick = (key) => {
    const v = data[key]
    return typeof v === "string" && v.trim() ? v.trim() : ""
  }
  const direct = pick("message") || pick("error")
  if (direct) return direct

  const d = data.details
  if (d == null) return ""

  if (typeof d === "string") {
    const re = /ErrorDetail\(string='([\s\S]*?)',\s*code=/g
    const parts = []
    let m
    while ((m = re.exec(d)) !== null) {
      parts.push(m[1].replace(/\\'/g, "'"))
    }
    if (parts.length) return parts.join(" ")
    const trimmed = d.trim()
    return trimmed
  }

  if (Array.isArray(d)) {
    return d
      .map((x) => (typeof x === "string" ? x : String(x)))
      .filter(Boolean)
      .join(" ")
      .trim()
  }

  if (typeof d === "object") {
    const parts = []
    for (const v of Object.values(d)) {
      if (!Array.isArray(v)) {
        if (v != null) parts.push(String(v))
        continue
      }
      for (const item of v) {
        if (item == null) continue
        if (typeof item === "string") parts.push(item)
        else if (typeof item === "object" && item !== null) {
          if (typeof item.string === "string") parts.push(item.string)
          else parts.push(String(item))
        } else parts.push(String(item))
      }
    }
    return parts.join(" ").trim()
  }

  return ""
}

const parseSafeInt = (value, fallback = 0) => {
  const n = parseInt(String(value ?? "").replace(/[^\d]/g, ""), 10)
  return Number.isFinite(n) ? n : fallback
}

const parseSafeAmount = (value, fallback = 0) => {
  const clean = String(value ?? "").replace(/[^\d.]/g, "")
  const n = parseFloat(clean)
  return Number.isFinite(n) ? n : fallback
}

const normalizeWholeNumberString = (value, fallback = "0") => {
  const clean = String(value ?? "").replace(/[^\d.]/g, "")
  const n = Math.trunc(parseFloat(clean))
  return Number.isFinite(n) ? String(n) : fallback
}

const ConfigurationTab = ({ readOnly = false }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.referralsPage || translations.en.referralsPage

  const [programEnabled, setProgramEnabled] = useState(true)
  const [discountEnabled, setDiscountEnabled] = useState(true)
  const [maxReferralsPerMonth, setMaxReferralsPerMonth] = useState("10")
  const [paymentHoldDays, setPaymentHoldDays] = useState("7")
  const [discountAmountSar, setDiscountAmountSar] = useState("10")
  const [saveSettingsSubmitting, setSaveSettingsSubmitting] = useState(false)

  const [rewardInfoList, setRewardInfoList] = useState([])
  const [rewardsLoading, setRewardsLoading] = useState(true)
  const [customCodesRows, setCustomCodesRows] = useState([])
  const [customCodesLoading, setCustomCodesLoading] = useState(true)
  const [newReferralCode, setNewReferralCode] = useState("")
  const [newCustomCodeEmail, setNewCustomCodeEmail] = useState("")
  const [addCustomCodeSubmitting, setAddCustomCodeSubmitting] = useState(false)

  const rewardsRows = useMemo(
    () => rewardInfoList.map((row) => mapRewardInfoRow(row, lang)),
    [rewardInfoList, lang],
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await ReferralsApi.getSettings()
        if (cancelled) return
        const payload = res?.data
        const result = payload?.result
        if (payload?.success && result) {
          setProgramEnabled(Boolean(result.is_active))
          setDiscountEnabled(Boolean(result.referred_user_discount_enabled))
          setMaxReferralsPerMonth(String(result.max_referrals_per_month ?? "0"))
          setPaymentHoldDays(String(result.reward_hold_period_days ?? "0"))
          setDiscountAmountSar(
            normalizeWholeNumberString(
              result.referred_user_discount_amount_sar,
              "0",
            ),
          )
        }
      } catch (e) {
        if (!cancelled) {
          message.error(
            e?.response?.data?.message ||
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load referral settings.",
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setRewardsLoading(true)
      try {
        const res = await ReferralsApi.getRewardInfo()
        if (cancelled) return
        const payload = res.data
        const list = payload?.result?.results
        if (payload?.success && Array.isArray(list)) {
          const sorted = [...list].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          setRewardInfoList(sorted)
        } else {
          setRewardInfoList([])
          message.error("Could not load reward configuration.")
        }
      } catch (e) {
        if (!cancelled) {
          setRewardInfoList([])
          message.error(
            e?.response?.data?.message ||
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load reward info.",
          )
        }
      } finally {
        if (!cancelled) setRewardsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setCustomCodesLoading(true)
      try {
        const res = await ReferralsApi.getCustomCodes()
        if (cancelled) return
        const payload = res.data
        const list = payload?.result?.results
        if (payload?.success && Array.isArray(list)) {
          setCustomCodesRows(list.map(mapCustomCodeRow))
        } else {
          setCustomCodesRows([])
          message.error("Could not load custom referral codes.")
        }
      } catch (e) {
        if (!cancelled) {
          setCustomCodesRows([])
          message.error(
            e?.response?.data?.message ||
              e?.response?.data?.error ||
              e?.message ||
              "Failed to load custom codes.",
          )
        }
      } finally {
        if (!cancelled) setCustomCodesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const isArabicLayout = lang === "ar"

  /** DOM column order stays dir=ltr; reverse cells for Arabic so Plan sits on the visual right. */
  const rewardsTableColumns = useMemo(() => {
    const cols = [
      { key: "plan", header: t.col_plan, field: "plan", tdClassName: "" },
      {
        key: "price",
        header: t.col_price_sar,
        field: "price",
        tdClassName: "",
      },
      {
        key: "monthly",
        header: t.col_monthly_reward,
        field: "monthly",
        tdClassName: "ref-config-highlight-blue",
      },
      {
        key: "annual",
        header: t.col_annual_reward,
        field: "annual",
        tdClassName: "ref-config-highlight-green",
      },
    ]
    return isArabicLayout ? [...cols].reverse() : cols
  }, [isArabicLayout, t])

  const customCodesTableColumns = useMemo(() => {
    const cols = [
      {
        key: "code",
        header: t.col_code,
        kind: "field",
        field: "code",
        ltr: true,
      },
      {
        key: "influencer",
        header: t.col_influencer,
        kind: "field",
        field: "influencer",
      },
      { key: "status", header: t.col_status, kind: "status" },
    ]
    return isArabicLayout ? [...cols].reverse() : cols
  }, [isArabicLayout, t])

  const configTableClassName = `ref-config-table${isArabicLayout ? " ref-config-table--ar-locale" : ""}`

  const handleAddCustomCode = useCallback(async () => {
    if (readOnly) return
    const code = newReferralCode.trim()
    const email = newCustomCodeEmail.trim()
    if (!code) {
      message.warning("Enter a referral code.")
      return
    }
    if (!email) {
      message.warning("Enter an email address.")
      return
    }
    if (!isValidEmail(email)) {
      message.warning("Enter a valid email address.")
      return
    }
    setAddCustomCodeSubmitting(true)
    try {
      const res = await ReferralsApi.createCustomCode({
        referral_code: code,
        email,
      })
      const payload = res.data
      if (payload?.success && payload.result) {
        const row = mapCustomCodeRow(payload.result)
        setCustomCodesRows((prev) => {
          const next = prev.filter((r) => r.key !== row.key)
          next.push(row)
          return next.sort((a, b) => Number(a.key) - Number(b.key))
        })
        setNewReferralCode("")
        setNewCustomCodeEmail("")
        message.success("Custom referral code added.")
      } else {
        const errText =
          messageFromReferralErrorPayload(payload) ||
          "Could not add custom code."
        message.error(errText)
      }
    } catch (e) {
      const data = e?.response?.data
      const errText =
        messageFromReferralErrorPayload(data) ||
        data?.message ||
        data?.error ||
        e?.message ||
        "Failed to add custom code."
      message.error(errText)
    } finally {
      setAddCustomCodeSubmitting(false)
    }
  }, [newReferralCode, newCustomCodeEmail, readOnly])

  const handleSaveSettings = useCallback(async () => {
    if (readOnly) return
    const payload = {
      max_referrals_per_month: parseSafeInt(maxReferralsPerMonth, 0),
      reward_hold_period_days: parseSafeInt(paymentHoldDays, 0),
      referred_user_discount_enabled: Boolean(discountEnabled),
      referred_user_discount_amount_sar: parseSafeAmount(
        discountAmountSar,
        0,
      ).toFixed(2),
      is_active: Boolean(programEnabled),
    }

    setSaveSettingsSubmitting(true)
    try {
      const res = await ReferralsApi.updateSettings(payload)
      if (res?.status === 200 || res?.status === 201) {
        message.success("Referral settings updated successfully.")
      } else {
        message.error("Could not save referral settings.")
      }
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to update referral settings.",
      )
    } finally {
      setSaveSettingsSubmitting(false)
    }
  }, [
    maxReferralsPerMonth,
    paymentHoldDays,
    discountEnabled,
    discountAmountSar,
    programEnabled,
    readOnly,
  ])

  return (
    <div className="ref-config-view">
      <div className="referrals-subheader ref-config-header-row">
        <h2>{t.config_title}</h2>
        <button
          className="ref-button-primary ref-config-save-btn"
          type="button"
          onClick={handleSaveSettings}
          disabled={readOnly || saveSettingsSubmitting}
          title={readOnly ? t.read_only_hint : undefined}
        >
          <img src="/referrals/floppy-disk.svg" alt="" width={14} height={14} />
          {saveSettingsSubmitting ? t.saving : t.save_changes}
        </button>
      </div>

      <div className="ref-panel ref-config-card">
        <div className="ref-config-card-head-config">
          <div>
            <h3 className="program-status-conf">{t.program_status}</h3>
            <span>{t.program_status_hint}</span>
          </div>
          <label
            className="ref-config-toggle"
            title={readOnly ? t.read_only_hint : undefined}
          >
            <input
              type="checkbox"
              checked={programEnabled}
              disabled={readOnly}
              onChange={(e) => setProgramEnabled(e.target.checked)}
            />
            <span className="ref-config-toggle-slider" />
          </label>
        </div>
      </div>

      <div className="ref-panel ref-config-card">
        <div className="ref-config-card-head">
          <div>
            <h3>{t.rewards_earning_rules}</h3>
          </div>
        </div>
        <div className="ref-config-table-wrap">
          {rewardsLoading ? (
            <div className="ref-config-rewards-loading">
              <Spin />
            </div>
          ) : (
            <table className={configTableClassName} dir="ltr">
              <thead>
                <tr>
                  {rewardsTableColumns.map((col) => (
                    <th key={col.key}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rewardsRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ref-config-table-empty">
                      {t.config_no_plans}
                    </td>
                  </tr>
                ) : (
                  rewardsRows.map((row) => (
                    <tr key={row.key}>
                      {rewardsTableColumns.map((col) => (
                        <td
                          key={col.key}
                          className={col.tdClassName || undefined}
                        >
                          <span className="ref-config-ltr-cell" dir="ltr">
                            {row[col.field]}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="ref-panel ref-config-card">
        <div className="ref-config-card-head">
          <div>
            <h3>{t.referral_limits_controls}</h3>
          </div>
        </div>
        <div className="ref-config-grid">
          <div className="ref-config-field referral-limits-controls">
            <label htmlFor="ref-max-referrals-month">
              {t.max_referrals_per_month}
            </label>
            <input
              id="ref-max-referrals-month"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={maxReferralsPerMonth}
              disabled={readOnly}
              onChange={(e) => setMaxReferralsPerMonth(e.target.value)}
            />
            <p>{t.max_referrals_hint}</p>
          </div>
          <div className="ref-config-field referral-limits-controls">
            <label htmlFor="ref-payment-hold-days">{t.payment_hold_days}</label>
            <input
              id="ref-payment-hold-days"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={paymentHoldDays}
              disabled={readOnly}
              onChange={(e) => setPaymentHoldDays(e.target.value)}
            />
            <p>{t.payment_hold_hint}</p>
          </div>
        </div>
      </div>

      <div className="ref-panel ref-config-card">
        <div className="ref-config-card-head">
          <div>
            <h3>{t.referred_user_discount}</h3>
            <p className="offer-instant-discount">
              {t.referred_user_discount_hint}
            </p>
          </div>
          <label
            className="ref-config-toggle"
            title={readOnly ? t.read_only_hint : undefined}
          >
            <input
              type="checkbox"
              checked={discountEnabled}
              disabled={readOnly}
              onChange={(e) => setDiscountEnabled(e.target.checked)}
            />
            <span className="ref-config-toggle-slider" />
          </label>
        </div>
        <div className="ref-config-grid ref-config-grid-single">
          <div className="ref-config-field">
            <label htmlFor="ref-discount-amount-sar">
              {t.discount_amount_sar}
            </label>
            <input
              id="ref-discount-amount-sar"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={discountAmountSar}
              disabled={readOnly || !discountEnabled}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/[^\d]/g, "")
                setDiscountAmountSar(digitsOnly)
              }}
            />
            <p className="instant-discount">{t.discount_first_purchase_hint}</p>
          </div>
        </div>
      </div>

      <div className="ref-panel ref-config-card">
        <div className="ref-config-card-head">
          <div>
            <h3>{t.custom_codes_title}</h3>
          </div>
        </div>

        <div className="ref-config-custom-controls">
          <div className="ref-config-field">
            <label htmlFor="ref-new-referral-code">{t.referral_code}</label>
            <input
              id="ref-new-referral-code"
              type="text"
              placeholder={t.placeholder_enter_code}
              value={newReferralCode}
              disabled={readOnly || addCustomCodeSubmitting}
              onChange={(e) =>
                setNewReferralCode(e.target.value.toUpperCase())
              }
            />
          </div>

          <div className="ref-config-field">
            <label htmlFor="ref-new-custom-code-email">{t.influencer_name}</label>
            <input
              id="ref-new-custom-code-email"
              type="email"
              autoComplete="email"
              placeholder={t.placeholder_select_influencer}
              value={newCustomCodeEmail}
              disabled={readOnly || addCustomCodeSubmitting}
              onChange={(e) => setNewCustomCodeEmail(e.target.value)}
            />
          </div>

          <button
            className="ref-button-primary ref-config-add-btn"
            type="button"
            disabled={readOnly || addCustomCodeSubmitting}
            title={readOnly ? t.read_only_hint : undefined}
            onClick={handleAddCustomCode}
          >
            {t.add_custom_code}
          </button>
        </div>

        <div className="ref-config-table-wrap">
          {customCodesLoading ? (
            <div className="ref-config-rewards-loading">
              <Spin />
            </div>
          ) : (
            <table
              className={`${configTableClassName} ref-config-custom-table`}
              dir="ltr"
            >
              <thead>
                <tr>
                  {customCodesTableColumns.map((col) => (
                    <th key={col.key}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customCodesRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="ref-config-table-empty">
                      {t.no_custom_codes}
                    </td>
                  </tr>
                ) : (
                  customCodesRows.map((row) => (
                    <tr key={row.key}>
                      {customCodesTableColumns.map((col) => {
                        if (col.kind === "status") {
                          return (
                            <td key={col.key}>
                              <span
                                className={
                                  row.isActive
                                    ? "ref-config-status-pill"
                                    : "ref-config-status-pill ref-config-status-pill-inactive"
                                }
                              >
                                {displayCustomCodeStatus(
                                  row.statusLabel,
                                  lang,
                                  t,
                                )}
                              </span>
                            </td>
                          )
                        }
                        const cell = row[col.field]
                        return (
                          <td key={col.key}>
                            {col.ltr ? (
                              <span className="ref-config-ltr-cell" dir="ltr">
                                {cell}
                              </span>
                            ) : (
                              cell
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default ConfigurationTab
